import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Phone, X, CalendarClock, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import SpyCleanLogo from "./SpyCleanLogo";

const LEISTUNGEN = [
  { label: "Haushaltsreinigung", to: "/leistungen/haushaltsreinigung" },
  { label: "Büroreinigung", to: "/leistungen/bueroreinigung" },
  { label: "Fensterreinigung", to: "/leistungen/glasreinigung" },
  { label: "Grundreinigung", to: "/leistungen/grundreinigung" },
  { label: "Umzugreinigung", to: "/leistungen/umzugreinigung" },
  { label: "Baureinigung", to: "/leistungen/baureinigung" },
  { label: "Praxisreinigung", to: "/leistungen/praxisreinigung" },
  { label: "Treppenhausreinigung", to: "/leistungen/treppenhausreinigung" },
  { label: "Steinreinigung", to: "/leistungen/steinreinigung" },
  { label: "Polster- & Teppichreinigung", to: "/leistungen/polsterreinigung" },
  { label: "Wäsche- & Bügelservice", to: "/leistungen/buegelservice" },
];

const STANDORTE = [
  { label: "München", to: "/standorte/" },
  { label: "Gräfelfing", to: "/standorte/graefelfing" },
  { label: "Planegg", to: "/standorte/planegg" },
  { label: "Gauting", to: "/standorte/gauting" },
  { label: "Pullach im Isartal", to: "/standorte/pullach" },
  { label: "Neuried", to: "/standorte/neuried" },
];

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileLeistungenOpen, setMobileLeistungenOpen] = useState(false);
  const [mobileStandorteOpen, setMobileStandorteOpen] = useState(false);
  const location = useLocation();
  const pathname = location.pathname;
  // Route-specific CTA label + destination. Each service page can preset the
  // /preise calculator with the correct customer type + service. Default for
  // all other routes is the generic calculator entry.
  const routeCta = (() => {
    if (pathname === "/leistungen/haushaltsreinigung")
      return {
        label: "Angebot anfordern",
        to: "/preise?kundentyp=Privat&service=Haushaltsreinigung",
      };
    if (pathname === "/leistungen/bueroreinigung")
      return {
        label: "Angebot anfordern",
        to: "/preise?kundentyp=Gewerbe&service=B%C3%BCroreinigung",
      };
    if (pathname === "/leistungen/praxisreinigung")
      return {
        label: "Angebot anfordern",
        to: "/preise?kundentyp=Gewerbe&service=Praxisreinigung",
      };
    if (pathname === "/leistungen/treppenhausreinigung")
      return {
        label: "Angebot anfordern",
        to: "/preise?kundentyp=Gewerbe&service=Treppenhausreinigung",
      };
    if (pathname === "/leistungen/grundreinigung")
      return {
        label: "Angebot anfordern",
        to: "/preise?kundentyp=Privat&service=Grundreinigung",
      };
    if (pathname === "/leistungen/fensterreinigung")
      return {
        label: "Angebot anfordern",
        to: "/preise?kundentyp=Privat&service=Fensterreinigung",
      };
    if (pathname === "/leistungen/glasreinigung")
      return {
        label: "Angebot anfordern",
        to: "/preise?kundentyp=Privat&service=Fensterreinigung",
      };
    if (pathname === "/leistungen/buegelservice")
      return {
        label: "Angebot anfordern",
        to: "/preise?kundentyp=Privat&service=Buegelservice",
      };
    if (pathname === "/leistungen/steinreinigung")
      return {
        label: "Angebot anfordern",
        to: "/preise?kundentyp=Privat&service=Steinreinigung",
      };
    if (pathname === "/leistungen/polsterreinigung")
      return {
        label: "Angebot anfordern",
        to: "/preise?kundentyp=Privat&service=Polsterreinigung",
      };
    if (pathname === "/leistungen/baureinigung")
      return {
        label: "Angebot anfordern",
        to: "/preise?kundentyp=Gewerbe&service=Bauendreinigung",
      };
    if (pathname === "/leistungen/umzugreinigung")
      return {
        label: "Angebot anfordern",
        to: "/preise?kundentyp=Privat&service=Umzugreinigung",
      };
    return { label: "Angebot anfordern", to: "/preise" };
  })();
  const headerCtaLabel = routeCta.label;
  const headerCtaTo = routeCta.to;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on Escape
  useEffect(() => {
    if (!isMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isMenuOpen]);

  // Close mobile menu on outside click
  useEffect(() => {
    if (!isMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (
        !t.closest("#mobile-nav-menu") &&
        !t.closest('[aria-label="Menü umschalten"]')
      ) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [isMenuOpen]);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const isActive = (path: string) => location.pathname === path;
  const isLeistungActive = LEISTUNGEN.some((l) => location.pathname === l.to);
  const isStandortActive = location.pathname.startsWith("/standorte");

  const activeClass = (path: string) =>
    isActive(path)
      ? "font-bold text-primary after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-secondary"
      : "";

  return (
    <header className="sticky top-0 z-50 w-full">
      {/* Main white navigation */}
      <div
        className="bg-white transition-all duration-300"
        style={{
          boxShadow: scrolled ? "0 6px 24px rgba(4, 52, 112, 0.10)" : "none",
          borderBottom: scrolled
            ? "none"
            : "1px solid hsl(var(--border) / 0.6)",
        }}
      >
        <div className="flex h-[88px] md:h-[104px] items-center justify-between max-w-[1480px] mx-auto px-4 md:px-10">
          {/* Logo */}
          <Link
            to="/"
            className="flex shrink-0 items-center transition-transform hover:scale-[1.02] duration-300"
            aria-label="Spy Clean GmbH Startseite"
          >
            <SpyCleanLogo
              alt="Spy Clean GmbH - Perfekt bis ins Detail"
              width="2007"
              height="783"
              className="h-[78px] w-[200px] md:h-[94px] md:w-[240px]"
            />
          </Link>

          {/* Desktop Nav */}
          <nav
            className="hidden xl:flex xl:items-center xl:gap-7 2xl:gap-9 text-[15px] font-medium text-primary"
            style={{ whiteSpace: "nowrap" }}
          >
            {/* Startseite */}
            <Link
              to="/"
              className={`transition-colors duration-200 hover:text-secondary relative py-2 whitespace-nowrap ${activeClass("/")}`}
            >
              Startseite
            </Link>

            {/* Leistungen dropdown */}
            <div className="group relative">
              <span
                className={`cursor-pointer transition-colors duration-200 hover:text-secondary py-2 inline-flex items-center gap-1 whitespace-nowrap ${isLeistungActive ? "font-bold text-primary" : ""}`}
              >
                Leistungen
                <ChevronDown className="w-3.5 h-3.5" />
              </span>
              <div className="absolute left-1/2 -translate-x-1/2 top-full hidden group-hover:block pt-1">
                <div className="w-[340px] rounded-xl border border-border bg-popover shadow-lg overflow-hidden">
                  <div className="py-2">
                    {LEISTUNGEN.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        className={`block px-5 py-[11px] text-[15px] font-medium transition-colors duration-150 ${
                          location.pathname === item.to
                            ? "text-secondary bg-secondary/5"
                            : "text-primary hover:text-secondary hover:bg-muted/50"
                        }`}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                  <div className="h-px bg-border" />
                  <Link
                    to="/leistungen"
                    className="block px-5 py-3 text-[14px] font-semibold text-primary hover:text-secondary hover:bg-muted/50 transition-colors duration-150"
                  >
                    Alle Leistungen ansehen &rarr;
                  </Link>
                </div>
              </div>
            </div>

            {/* Einsatzgebiete dropdown */}
            <div className="group relative">
              <Link
                to="/standorte/"
                className={`cursor-pointer transition-colors duration-200 hover:text-secondary py-2 inline-flex items-center gap-1 whitespace-nowrap ${isStandortActive ? "font-bold text-primary" : ""}`}
              >
                Einsatzgebiete
                <ChevronDown className="w-3.5 h-3.5" />
              </Link>
              <div className="absolute left-0 top-full hidden group-hover:block pt-1">
                <div className="w-56 rounded-xl border border-border bg-popover shadow-lg overflow-hidden">
                  <div className="py-2">
                    {STANDORTE.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        className="block px-4 py-2 text-sm font-medium text-primary hover:text-secondary hover:bg-muted/50 transition-colors duration-150"
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                  <div className="h-px bg-border" />
                  <Link
                    to="/standorte/"
                    className="block px-4 py-3 text-sm font-semibold text-primary hover:text-secondary hover:bg-muted/50 transition-colors duration-150"
                  >
                    Alle Einsatzgebiete &rarr;
                  </Link>
                </div>
              </div>
            </div>

            {/* Simple nav links */}
            <Link
              to="/blog"
              className={`transition-colors duration-200 hover:text-secondary relative py-2 whitespace-nowrap ${activeClass("/blog")}`}
            >
              Blog
            </Link>
          </nav>

          {/* Right side: Termin, CTA + phone + hamburger */}
          <div className="flex items-center gap-3 lg:gap-5">
            {/* Termin verwalten — desktop only, subtly highlighted */}
            <Link
              to="/termin"
              className="hidden lg:inline-flex items-center gap-1.5 text-[14px] font-semibold text-primary whitespace-nowrap rounded-lg border border-primary/15 bg-primary/[0.03] px-3.5 py-2 hover:bg-primary/[0.07] hover:border-primary/25 transition-all duration-200"
            >
              <CalendarClock className="h-4 w-4 text-secondary" />
              Termin verwalten
            </Link>

            {/* Yellow CTA + phone number */}
            <div className="hidden lg:flex items-center gap-4">
              <Button
                asChild
                className="bg-secondary text-secondary-foreground hover:bg-secondary/85 font-semibold rounded-xl px-5 py-2.5 text-sm transition-all duration-200 hover:-translate-y-0.5 shadow-md hover:shadow-lg whitespace-nowrap"
              >
                <Link to={headerCtaTo}>{headerCtaLabel}</Link>
              </Button>
              <a
                href="tel:+4917664017221"
                aria-label="Spy Clean jetzt anrufen: +49 176 640 17 221"
                title="Jetzt anrufen"
                className="flex items-center gap-1.5 text-[14px] font-medium text-primary hover:text-secondary transition-colors duration-200 whitespace-nowrap"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/8 transition-colors duration-200 group-hover:bg-primary/15">
                  <Phone className="h-4 w-4 text-primary" />
                </span>
                <span className="hidden 2xl:inline">Jetzt anrufen</span>
              </a>
            </div>

            {/* Hamburger — visible below xl (1280px) */}
            <button
              className="flex h-[42px] w-[42px] items-center justify-center xl:hidden text-primary"
              onClick={toggleMenu}
              aria-label="Menü umschalten"
              aria-expanded={isMenuOpen}
              aria-controls="mobile-nav-menu"
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      {isMenuOpen && (
        <div
          id="mobile-nav-menu"
          className="fixed left-0 top-[88px] md:top-[104px] z-50 h-[calc(100dvh-88px)] md:h-[calc(100dvh-104px)] w-full overflow-y-auto overscroll-contain bg-white text-primary pb-20 xl:hidden border-t"
        >
          <nav className="flex flex-col pt-7 text-lg font-medium px-5">
            {/* Startseite */}
            <Link
              to="/"
              onClick={toggleMenu}
              className="border-b py-3 font-bold text-primary"
            >
              Startseite
            </Link>

            {/* Leistungen accordion */}
            <div className="border-b pb-2">
              <button
                onClick={() => setMobileLeistungenOpen(!mobileLeistungenOpen)}
                className="flex items-center justify-between w-full font-bold text-primary py-2"
              >
                Leistungen
                <ChevronDown
                  className={`w-5 h-5 transition-transform ${mobileLeistungenOpen ? "rotate-180" : ""}`}
                />
              </button>
              {mobileLeistungenOpen && (
                <div className="flex flex-col gap-0.5 pl-2 pb-2">
                  {LEISTUNGEN.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={toggleMenu}
                      className="py-2 pl-3 text-base text-muted-foreground hover:text-primary transition-colors"
                    >
                      {item.label}
                    </Link>
                  ))}
                  <Link
                    to="/leistungen"
                    onClick={toggleMenu}
                    className="py-2 pl-3 text-base font-semibold text-primary"
                  >
                    Alle Leistungen ansehen &rarr;
                  </Link>
                </div>
              )}
            </div>

            {/* Einsatzgebiete accordion */}
            <div className="border-b pb-2">
              <button
                onClick={() => setMobileStandorteOpen(!mobileStandorteOpen)}
                className="flex items-center justify-between w-full font-bold text-primary py-2"
              >
                Einsatzgebiete
                <ChevronDown
                  className={`w-5 h-5 transition-transform ${mobileStandorteOpen ? "rotate-180" : ""}`}
                />
              </button>
              {mobileStandorteOpen && (
                <div className="flex flex-col gap-0.5 pl-2 pb-2">
                  {STANDORTE.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={toggleMenu}
                      className="py-2 pl-3 text-base text-muted-foreground hover:text-primary transition-colors"
                    >
                      {item.label}
                    </Link>
                  ))}
                  <Link
                    to="/standorte/"
                    onClick={toggleMenu}
                    className="py-2 pl-3 text-base font-semibold text-primary"
                  >
                    Alle Einsatzgebiete &rarr;
                  </Link>
                </div>
              )}
            </div>

            <Link to="/blog" onClick={toggleMenu} className="border-b py-3">
              Blog
            </Link>
            {/* Termin verwalten — dezent am Ende */}
            <Link
              to="/termin"
              onClick={toggleMenu}
              className="inline-flex items-center gap-2 text-base font-medium text-primary border border-border bg-muted/30 rounded-lg px-4 py-3 mt-4"
            >
              <CalendarClock className="h-5 w-5" />
              Termin verwalten
            </Link>

            {/* CTA */}
            <div className="pt-2">
              <Button
                className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 h-12 text-lg font-semibold rounded-lg"
                onClick={() => {
                  toggleMenu();
                }}
              >
                <Link to={headerCtaTo}>{headerCtaLabel}</Link>
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
